/**
 * SeasonOverview - Season statistics dashboard
 * 
 * Thin orchestrator that delegates to widget components.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Download, FileText, FileSpreadsheet, Loader2, BarChart3, Shield } from 'lucide-react';

import { formatSeasonDate } from '../components/season/utils';
import { QuickStatsGrid } from '../components/season/QuickStatsGrid';
import { CompetitionBreakdown, CupProgressWidget, FormationAnalysis, KickOffTimeBreakdown } from '../components/season/CompetitionWidgets';
import { TopPerformers } from '../components/season/TopPerformers';
import { MatchResultsList } from '../components/season/MatchResultsList';
import { SquadChangesWidget } from '../components/season/SquadChangesWidget';
import { AdditionalStats } from '../components/season/AdditionalStats';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeasonOverview() {
  const { user } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => { fetchSeasons(); }, []);
  useEffect(() => { if (selectedSeasonId) fetchSeasonReport(selectedSeasonId); }, [selectedSeasonId]);

  const fetchSeasons = async () => {
    try {
      const response = await axios.get(`${API}/seasons`);
      setSeasons(response.data);
      if (user?.current_season_id) setSelectedSeasonId(user.current_season_id);
      else if (response.data.length > 0) setSelectedSeasonId(response.data[0].id);
    } catch (error) {
      console.error('Failed to fetch seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasonReport = async (seasonId) => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/reports/season/${seasonId}/data`);
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch season report:', error);
      toast.error('Failed to load season data');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedSeasonId) return;
    setExporting(format);
    try {
      const response = await axios.get(`${API}/reports/season/${selectedSeasonId}/${format}`, {
        responseType: 'blob'
      });
      const selectedSeason = seasons.find(s => s.id === selectedSeasonId);
      const mimeType = format === 'pdf' ? 'application/pdf' : undefined;
      const url = window.URL.createObjectURL(new Blob([response.data], mimeType ? { type: mimeType } : undefined));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedSeason?.name || 'Season'}_Report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  if (seasons.length === 0) {
    return (
      <Layout>
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Seasons Available</h2>
          <p className="text-gray-500">Create a season first to view the overview.</p>
        </div>
      </Layout>
    );
  }

  const stats = reportData?.stats || {};
  const selectedSeason = seasons.find(s => s.id === selectedSeasonId);

  return (
    <Layout>
      <div className="space-y-8 page-slide-in" data-testid="season-overview-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Season Overview</h1>
            <p className="text-gray-500 mt-1">View detailed statistics for any season</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={selectedSeasonId} onValueChange={setSelectedSeasonId}>
              <SelectTrigger className="w-[160px] sm:w-[200px] rounded-xl" data-testid="season-selector">
                <SelectValue placeholder="Select a season" />
              </SelectTrigger>
              <SelectContent>
                {seasons.map((season) => (
                  <SelectItem key={season.id} value={season.id}>
                    {season.name}{season.id === user?.current_season_id && ' (Active)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => handleExport('csv')} disabled={exporting || !selectedSeasonId} className="rounded-xl" data-testid="export-csv-btn">
              {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-green-600" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting || !selectedSeasonId} className="rounded-xl" data-testid="export-pdf-btn">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-red-600" />}
            </Button>
          </div>
        </div>

        {loadingReport ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : reportData ? (
          <div className="flex flex-col gap-10">
            {/* Season Info Banner — uses the coach's primary (menu) colour. */}
            <Card
              className="text-white"
              style={{
                background: `linear-gradient(to right, ${user?.menu_color || '#059669'}, color-mix(in srgb, ${user?.menu_color || '#059669'} 75%, black))`,
              }}
              data-testid="season-banner"
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    {user?.club_badge ? (
                      <img
                        src={user.club_badge.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.club_badge}` : user.club_badge}
                        alt={`${reportData.team_name} badge`}
                        className="w-14 h-14 sm:w-16 sm:h-16 object-contain shrink-0"
                        data-testid="season-banner-club-badge"
                      />
                    ) : (
                      <Shield className="w-14 h-14 sm:w-16 sm:h-16 text-white shrink-0" strokeWidth={1.5} />
                    )}
                    <div className="min-w-0">
                      <h2 className="text-lg sm:text-2xl font-bold truncate">{reportData.team_name}</h2>
                      <p className="text-white/85 text-sm sm:text-base">{selectedSeason?.name}</p>
                      <p className="text-xs sm:text-sm text-white/75 mt-1">
                        {formatSeasonDate(selectedSeason?.start_date)} - {selectedSeason?.end_date ? formatSeasonDate(selectedSeason?.end_date) : 'Present'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-2xl sm:text-4xl font-bold">{stats.win_percentage || 0}%</p>
                    <p className="text-white/75 text-xs sm:text-base">Win Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <QuickStatsGrid stats={stats} />
            <CompetitionBreakdown stats={stats} />
            <CupProgressWidget cupProgress={reportData.cup_progress} />
            <FormationAnalysis formationAnalysis={reportData.formation_analysis} />
            <KickOffTimeBreakdown kickOffTimeBreakdown={reportData.kick_off_time_breakdown} />
            <TopPerformers topScorers={reportData.top_scorers} topAssisters={reportData.top_assisters} topStarts={reportData.top_starts} topYellowCards={reportData.top_yellow_cards} />
            <MatchResultsList completedMatches={reportData.completed_matches} players={reportData.players} />
            <SquadChangesWidget squadChanges={reportData.squad_changes} avgSquadChanges={reportData.avg_squad_changes} />
            <AdditionalStats stats={stats} />
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
